import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COUNTRIES, getProvinceStates } from "@/lib/address-options";

interface AddressFieldsProps {
  prefix: string;
  values: {
    street_address?: string;
    city?: string;
    province_state?: string;
    postal_zip_code?: string;
    country?: string;
  };
  onChange: (field: string, value: string) => void;
  onBatchChange?: (updates: Record<string, string>) => void;
  required?: boolean;
}

export function AddressFields({ prefix, values, onChange, onBatchChange, required = false }: AddressFieldsProps) {
  const provinceStates = getProvinceStates(values.country);

  const handleCountryChange = (value: string) => {
    // Check if we need to reset province
    const newProvinceStates = getProvinceStates(value);
    const currentProvinceValid = newProvinceStates.some(ps => ps.value === values.province_state);
    
    if (onBatchChange && !currentProvinceValid) {
      // Use batch update if available
      onBatchChange({ country: value, province_state: "" });
    } else {
      // Single update only
      onChange("country", value);
    }
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Label htmlFor={`${prefix}-street`}>Street Address {required && "*"}</Label>
        <Input
          id={`${prefix}-street`}
          value={values.street_address || ""}
          onChange={(e) => onChange("street_address", e.target.value)}
          placeholder="123 Main Street"
          required={required}
        />
      </div>
      <div>
        <Label htmlFor={`${prefix}-city`}>City {required && "*"}</Label>
        <Input
          id={`${prefix}-city`}
          value={values.city || ""}
          onChange={(e) => onChange("city", e.target.value)}
          placeholder="Toronto"
          required={required}
        />
      </div>
      <div>
        <Label htmlFor={`${prefix}-country`}>Country</Label>
        <Select
          value={values.country || undefined}
          onValueChange={handleCountryChange}
        >
          <SelectTrigger id={`${prefix}-country`}>
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRIES.map((country) => (
              <SelectItem key={country.value} value={country.value}>
                {country.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor={`${prefix}-province`}>Province/State</Label>
        <Select
          value={values.province_state || undefined}
          onValueChange={(value) => onChange("province_state", value)}
        >
          <SelectTrigger id={`${prefix}-province`}>
            <SelectValue placeholder="Select province/state" />
          </SelectTrigger>
          <SelectContent>
            {provinceStates.map((ps) => (
              <SelectItem key={ps.value} value={ps.value}>
                {ps.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor={`${prefix}-postal`}>Postal/Zip Code</Label>
        <Input
          id={`${prefix}-postal`}
          value={values.postal_zip_code || ""}
          onChange={(e) => onChange("postal_zip_code", e.target.value)}
          placeholder="M5V 1A1"
        />
      </div>
    </div>
  );
}

export function formatAddress(address: {
  street_address?: string;
  city?: string;
  province_state?: string;
  postal_zip_code?: string;
  country?: string;
}): string {
  const parts = [
    address.street_address,
    address.city,
    address.province_state,
    address.postal_zip_code,
    address.country,
  ].filter(Boolean);
  return parts.join(", ") || "No address";
}
